import React from 'react';
import { useOrder } from '../state/OrderContext';
import { useLocation } from '../state/LocationContext';
import { recommendationFor } from '../lib/recommendedBrands';
import { formatDistance, measurementSystemFor } from '../lib/geo';
import { regionFromLocale } from '../lib/countries';
import { PinIcon } from './icons';
// Brands whose logo this card can draw. The artwork itself is referenced from
// RecommendedStoreCard.css, NOT imported here, and that is deliberate: importing
// an image into JS makes Parcel emit a bare module specifier resolved at runtime
// through an <script type=importmap> entry, which failed to produce a usable src
// and rendered the logo broken. A `url()` in a stylesheet compiles to a plain
// relative path with no resolution step at all. Adding a brand therefore means
// adding a `--<id>` rule in the CSS beside this list.
const BRANDS_WITH_LOGOS = new Set(['aldi']);
import './RecommendedStoreCard.css';

// The shop we recommend, given its own card on Home.
//
// What this card is allowed to say, and the lines it does not cross:
//
//   It names a real branch the places provider actually returned, with that
//   branch's real distance, address and rating, and adds one sentence of our
//   own opinion, attributed as ours. That is the whole card.
//
//   It does NOT claim pickup. Nobody has checked whether this branch does
//   online pickup, so there is no "pickup" wording, no "best default for
//   pickup", and no badge. It does NOT claim we shop, order, or transfer a
//   list anywhere — the user carries the list themselves. And there is no
//   green "best value" pill: green reads as verified, and "best" is a
//   superlative with no data behind it. See CLAUDE.md, and the header of
//   lib/recommendedBrands.js.
//
// It renders ONLY when a lookup already found a recommended branch nearby and
// that result persisted. Home never asks for a location and never runs a
// lookup — that stays in StorePicker, where the user asked for it. So on a
// device that has not searched yet, this card is simply absent rather than
// advertising a shop we have no reason to think is in range.

export default function RecommendedStoreCard() {
  const { store, storeOptions, update } = useOrder();
  const location = useLocation();

  // The nearest recommended branch among results we already have. `storeOptions`
  // is ranked, so `find` is the closest one.
  const pick = (storeOptions ?? []).find((candidate) => recommendationFor(candidate));
  const recommendation = pick ? recommendationFor(pick) : null;
  if (!pick || !recommendation) return null;

  const hasLogo = BRANDS_WITH_LOGOS.has(recommendation.id);
  const system = measurementSystemFor(location.coords?.countryCode ?? regionFromLocale());
  const distance = formatDistance(pick.distanceMeters, {
    system,
    approximate: location.isApproximate,
  });
  const chosen = Boolean(store?.id && store.id === pick.id);

  return (
    <section className="card recommended-store">
      <p className="card__label">Recommended store</p>

      <div className="recommended-store__row">
        {/* A background image carries no alt text, and with the shop's name
            removed from the card this logo is the ONLY thing identifying it —
            so it is given an image role and the name as its label. Without
            that a screen reader gets "our pick, often the cheapest place to
            fill a whole list" with no idea which shop that is. */}
        {hasLogo && (
          <span
            className={`recommended-store__logo recommended-store__logo--${recommendation.id}`}
            role="img"
            aria-label={pick.name}
          />
        )}

        <div className="recommended-store__body">
          {/* The name is dropped when a logo already says it, and comes back
              when there is no logo — a brand added later without artwork must
              never render as an anonymous card. */}
          {!hasLogo && <p className="recommended-store__name">{pick.name}</p>}
          {/* Attributed, because this sentence is the one thing on the card
              that did not come from the provider. */}
          <p className="recommended-store__reason">
            <span className="recommended-store__pick-label">Our pick</span> {recommendation.reason}
          </p>
          <p className="meta-row recommended-store__meta">
            <PinIcon size={13} /> {distance}
            {pick.address ? ` · ${pick.address}` : ''}
          </p>
        </div>
      </div>

      {/* Selecting a store is not ordering and not a pickup booking: it decides
          which shop the recipe's "Shop this list" section points at, and
          nothing else happens until the user presses something there. */}
      {chosen ? (
        <p className="recommended-store__chosen">This is your selected store.</p>
      ) : (
        <button
          type="button"
          className="button button--primary recommended-store__use"
          onClick={() => update({ store: pick })}
        >
          Use {pick.name}
        </button>
      )}
    </section>
  );
}
